import { useState, useCallback, useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import { appAxios } from '@/network/base/appAxios';
import { toast } from 'sonner';
import { useUserStore } from '@/stores/useUser.store';
import StepperHeader from './StepperHeader';
import SuccessStep from './steps/SuccessStep';
import { STEP_COMPONENTS, STEP_META, buildSteps, buildSubmitPayload } from './stepperConfig';
import { useNavigate } from 'react-router-dom';

const { setUserRole } = useUserStore.getState();

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * PermitStepperModal — unified multi-step permit submission flow.
 *
 * Props:
 *  isOpen          – controls visibility
 *  onClose         – called when the user dismisses the modal
 *  permitChatId    – current permit chat ID
 *  projectId       – pre-linked project ID (optional; project_link step handles linking)
 *  messageMetadata – message.metadata from the chat bubble (for submitPermit payload)
 *  onComplete      – optional callback fired when the full flow finishes
 */
const PermitStepperModal = ({
  isOpen,
  onClose,
  permitChatId,
  projectId: initialProjectId = null,
  messageMetadata = {},
  onComplete,
}) => {
  const [fetchedSteps, setFetchedSteps] = useState(null);
  const [isFetchingSteps, setIsFetchingSteps] = useState(false);
  const navigate = useNavigate();
  // Use API-fetched steps; fall back to defaults while loading or on error.
  const steps = buildSteps(fetchedSteps ?? []);

  const [currentIndex, setCurrentIndex] = useState(0);
  // Shared context accumulated across steps.
  const [context, setContext] = useState({
    projectId: initialProjectId,
    ackData: null,
    permitId: null,
    userId: null,
  });
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  // Reset state and fetch stepper config whenever the modal opens.
  useEffect(() => {
    if (!isOpen) return;

    setContext({ projectId: initialProjectId, ackData: null, permitId: null, userId: null });
    setIsSuccess(false);
    setIsTransitioning(false);
    setCurrentIndex(0);
    setFetchedSteps(null);
    setIsFetchingSteps(true);

    appAxios
      .get('/PermitOnboardStepper/v1.0/get-permit-onboard-stepper', { params: { permitChatId } })
      .then(async res => {
        const apiSteps = res.data?.data?.steps ?? res.data?.data ?? [];

        if (res.data?.data?.project_id) {
          const submitResult = await doSubmitPermit(res.data.data.project_id);
          if (submitResult?.action === 'show_acknowledgement' && submitResult?.content) {
            setContext(prev => ({ ...prev, ackData: submitResult.content }));
          } else if (submitResult?.status === 'Success' && submitResult?.data) {
            setContext(prev => ({ ...prev, permitId: submitResult.data._id, userId: submitResult.data.user_id }));
          }
        }

        const normalized = Array.isArray(apiSteps) ? apiSteps : [];
        setFetchedSteps(normalized);

        // Advance past already-completed steps — rely solely on the completed flag.
        const builtSteps = buildSteps(normalized);
        const firstPending = builtSteps.findIndex(s => !s.completed);
        if (firstPending === -1) setIsSuccess(true);
        else if (firstPending > 0) setCurrentIndex(firstPending);
      })
      .catch(() => setFetchedSteps([]))
      .finally(() => setIsFetchingSteps(false));
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateContext = useCallback(updates => setContext(prev => ({ ...prev, ...updates })), []);

  // ── Mark stepper step complete on backend ──────────────────────────────────
  const updateStepperProgress = async stepKey => {
    try {
      await appAxios.post('/PermitOnboardStepper/v1.0/update-permit-onboard-stepper', {
        permitChatId,
        stepKey,
      });
    } catch {
      // Non-critical — don't block navigation
    }
  };

  // ── submitPermit wrapper ────────────────────────────────────────────────────
  const doSubmitPermit = async projectId => {
    try {
      const payload = buildSubmitPayload(messageMetadata);
      const res = await appAxios.post(`/Permit/v1.0/submitPermit/${projectId}/${permitChatId}`, payload);
      return {
        action: res.data?.ui_action,
        content: res.data?.content,
        status: res.data?.status,
        data: res.data?.data,
      };
    } catch (err) {
      const errData = err?.response?.data;
      return {
        action: errData?.ui_action,
        content: errData?.content,
        status: 'error',
        httpStatus: err?.response?.status,
        data: null,
      };
    }
  };

  // ── Between-step navigation helpers ────────────────────────────────────────
  const jumpToKey = useCallback(
    key => {
      const idx = steps.findIndex(s => s.key === key);
      if (idx >= 0) setCurrentIndex(idx);
    },
    [steps]
  );

  const advanceOrFinish = useCallback(
    fromIndex => {
      if (fromIndex >= steps.length - 1) {
        setIsSuccess(true);
      } else {
        setCurrentIndex(fromIndex + 1);
      }
    },
    [steps.length]
  );

  // ── Main step-complete handler (the "parent onSubmit") ──────────────────────
  const handleChildSubmit = async (stepData = {}) => {
    const completedKey = steps[currentIndex]?.key;
    const mergedContext = { ...context, ...stepData };
    updateContext(stepData);

    try {
      // ── Authorization ─────────────────────────────────────────────────────
      if (completedKey === 'authorization') {
        setIsTransitioning(true);
        await updateStepperProgress('authorization');
        try {
          const res = await appAxios.get('/Authorization/v1.0/getUserRole');
          const role = res.data?.data;
          if (role) setUserRole(role);
        } catch {
          // Non-critical — role will be re-fetched on next page load.
        }
        setIsTransitioning(false);
        advanceOrFinish(currentIndex);
        return;
      }

      // ── Project Link ──────────────────────────────────────────────────────
      // After the project is linked, call submitPermit to kick off server processing.
      if (completedKey === 'project_link') {
        setIsTransitioning(true);
        await updateStepperProgress('project_link');
        const result = await doSubmitPermit(mergedContext.projectId);

        if (result.action === 'show_acknowledgement' && result.content) {
          updateContext({ ackData: result.content });
          // Jump to general_disclaimer if it exists, otherwise acknowledgement.
          const target = steps.find(s => s.key === 'general_disclaimer') ? 'general_disclaimer' : 'acknowledgement';
          jumpToKey(target);
          setIsTransitioning(false);
          return;
        }

        if (result.action === 'show_file_upload_form') {
          jumpToKey('file_upload');
          setIsTransitioning(false);
          return;
        }

        if (result.status === 'Success' && result.data) {
          updateContext({ permitId: result.data._id, userId: result.data.user_id });
          jumpToKey('base_fees_payment');
          setIsTransitioning(false);
          return;
        }

        if (result.status === 'error' && result.httpStatus === 400) {
          // Already submitted — jump straight to payment or success.
          const payIdx = steps.findIndex(s => s.key === 'base_fees_payment');
          if (payIdx >= 0) setCurrentIndex(payIdx);
          else setIsSuccess(true);
          setIsTransitioning(false);
          return;
        }

        toast.error('Failed to process application. Please try again.');
        setIsTransitioning(false);
        return;
      }

      // ── File Upload ───────────────────────────────────────────────────────
      // After file upload, call submitPermit again to continue the flow.
      if (completedKey === 'file_upload') {
        setIsTransitioning(true);
        await updateStepperProgress('file_upload');
        const result = await doSubmitPermit(mergedContext.projectId);

        if (result.action === 'show_acknowledgement' && result.content) {
          updateContext({ ackData: result.content });
          const target = steps.find(s => s.key === 'general_disclaimer') ? 'general_disclaimer' : 'acknowledgement';
          jumpToKey(target);
          setIsTransitioning(false);
          return;
        }

        if (result.status === 'Success' && result.data) {
          updateContext({ permitId: result.data._id, userId: result.data.user_id });
          jumpToKey('base_fees_payment');
          setIsTransitioning(false);
          return;
        }

        if (result.status !== 'error') {
          // Unknown success-like response — advance normally.
          advanceOrFinish(currentIndex);
          setIsTransitioning(false);
          return;
        }

        toast.error('Failed to process application. Please try again.');
        setIsTransitioning(false);
        return;
      }

      // ── Acknowledgement ───────────────────────────────────────────────────
      // After the ack step's API call (change-acknowledgement-status) succeeds,
      // call submitPermit again to get the permitId for the payment step.
      if (completedKey === 'acknowledgement') {
        setIsTransitioning(true);
        await updateStepperProgress('acknowledgement');
        const result = await doSubmitPermit(mergedContext.projectId);

        if (result.status === 'Success' && result.data) {
          updateContext({ permitId: result.data._id, userId: result.data.user_id });
        } else if (result.status === 'error') {
          toast.error('Failed to finalize application. Please try again.');
          setIsTransitioning(false);
          return;
        }

        setIsTransitioning(false);
        advanceOrFinish(currentIndex);
        return;
      }

      // ── Default: just advance ─────────────────────────────────────────────
      await updateStepperProgress(completedKey);
      advanceOrFinish(currentIndex);
    } catch (err) {
      console.error('Stepper transition error:', err);
      toast.error('Something went wrong. Please try again.');
      setIsTransitioning(false);
    }
  };

  const handleBack = () => {
    if (currentIndex > 0) setCurrentIndex(i => i - 1);
  };

  const handleCheckStatus = async () => {
    try {
      const getPermitId = await appAxios.get(`/PermitChatGroup/v1.0/getPermitIdbyChatId`, { params: { permitChatId: permitChatId } });
      if (getPermitId.data?.status === 'Success') {
        onComplete?.();
        onClose();
        navigate(`/citypermit/project/chat/${permitChatId}/permit/${getPermitId.data.data}`);
      } else {
        toast.error('Failed to get status, Try Again Later');
      }
    } catch (error) {
      toast.error('Failed to check status , Try Again Later');
    }

  }


  const handleViewDashboard = async () => {
    onComplete?.();
    await handleCheckStatus();
  };

  // ── Render ────────────────────────────────────────────────────────────────
  if (!isOpen) return null;

  if (isFetchingSteps) {
    return (
      <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div className="bg-[#1a1a1a] rounded-xl w-full max-w-[440px] p-6 shadow-2xl flex flex-col items-center gap-3 py-14">
          <Loader2 className="w-7 h-7 animate-spin text-[#FEBA08]" />
          <p className="text-sm text-gray-400">Loading…</p>
        </div>
      </div>
    );
  }

  const currentStep = steps[currentIndex];
  const StepComponent = currentStep ? STEP_COMPONENTS[currentStep.key] : null;
  const meta = (currentStep && STEP_META[currentStep.key]) || { title: currentStep?.label ?? '', subtitle: '' };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-[#1a1a1a] rounded-xl w-full max-w-[440px] max-h-[90vh] overflow-y-auto no-scrollbar p-6 shadow-2xl relative">
        {isSuccess ? (
          <SuccessStep onClose={onClose} onViewDashboard={handleViewDashboard} />
        ) : (
          <>
            <StepperHeader
              steps={steps}
              currentIndex={currentIndex}
              title={meta.title}
              subtitle={meta.subtitle}
              onClose={onClose}
            />

            {isTransitioning ? (
              <div className="flex flex-col items-center justify-center py-14 gap-3">
                <Loader2 className="w-7 h-7 animate-spin text-[#FEBA08]" />
                <p className="text-sm text-gray-400">Processing…</p>
              </div>
            ) : StepComponent ? (
              <StepComponent
                onSubmit={handleChildSubmit}
                onBack={handleBack}
                context={context}
                permitChatId={permitChatId}
                isFirst={currentIndex === 0}
              />
            ) : (
              <p className="text-center text-gray-400 py-8 text-sm">Step not configured.</p>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default PermitStepperModal;
